import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, startOfMonth, endOfMonth, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'
import { Calendar as CalendarIcon, Loader2, FileText } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { cn } from '@/lib/utils'
import { getBillableSessions, createHonorariumNote } from '@/lib/api'
import { getErrorMessage } from '@/lib/error-messages'
import type { CourseSession } from '@/types/domain'

interface HonorariumGenerationSectionProps {
    trainerId: string
}

type BillableSession = CourseSession & {
    booking_count: number
    amount_cents: number
    courses?: { name: string }
}

export function HonorariumGenerationSection({ trainerId }: HonorariumGenerationSectionProps) {
    const queryClient = useQueryClient()
    const [dateRange, setDateRange] = useState<{
        from: Date
        to: Date
    }>({
        from: startOfMonth(new Date()),
        to: endOfMonth(new Date()),
    })
    const [fromOpen, setFromOpen] = useState(false)
    const [toOpen, setToOpen] = useState(false)
    const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set())

    const sessionsQuery = useQuery({
        queryKey: ['billable-sessions', trainerId, dateRange.from.toISOString(), dateRange.to.toISOString()],
        queryFn: () => getBillableSessions(trainerId, dateRange.from, dateRange.to),
    })

    const billableSessions = useMemo(() => (sessionsQuery.data ?? []) as (BillableSession & { is_billed?: boolean })[], [sessionsQuery.data])

    const createMutation = useMutation({
        mutationFn: (payload: {
            trainerId: string
            periodStart: Date
            periodEnd: Date
            items: { session_id: string; participant_count: number; amount_cents: number }[]
        }) => createHonorariumNote(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['billable-sessions'] })
            queryClient.invalidateQueries({ queryKey: ['honorarium-notes', trainerId] })
            toast.success('Honorarnote erfolgreich generiert')
            setSelectedSessionIds(new Set())
        },
        onError: (error) => {
            toast.error(`Fehler beim Generieren: ${getErrorMessage(error)}`)
        },
    })

    const totalAmount = useMemo(() => {
        return billableSessions
            .filter((s) => selectedSessionIds.has(s.id) && !s.is_billed)
            .reduce((sum, s) => sum + s.amount_cents, 0)
    }, [billableSessions, selectedSessionIds])

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedSessionIds(new Set(billableSessions.filter(s => !s.is_billed).map((s) => s.id)))
        } else {
            setSelectedSessionIds(new Set())
        }
    }

    const toggleSession = (sessionId: string) => {
        const next = new Set(selectedSessionIds)
        if (next.has(sessionId)) {
            next.delete(sessionId)
        } else {
            next.add(sessionId)
        }
        setSelectedSessionIds(next)
    }

    const handleGenerate = () => {
        if (selectedSessionIds.size === 0) {
            toast.error('Bitte wähle mindestens einen Termin aus.')
            return
        }

        const items = billableSessions
            .filter((s) => selectedSessionIds.has(s.id) && !s.is_billed)
            .map((s) => ({
                session_id: s.id,
                participant_count: s.booking_count,
                amount_cents: s.amount_cents,
            }))

        createMutation.mutate({
            trainerId,
            periodStart: dateRange.from,
            periodEnd: dateRange.to,
            items,
        })
    }

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>Honorarnote generieren</CardTitle>
                    <CardDescription>
                        Wähle den Zeitraum und die Termine aus, für die ein Honorar erstellt werden soll.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex flex-col md:flex-row gap-4 items-end">
                        <div className="space-y-2">
                            <Label>Von</Label>
                            <Popover open={fromOpen} onOpenChange={setFromOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={cn(
                                            'w-[240px] justify-start text-left font-normal',
                                            !dateRange.from && 'text-muted-foreground'
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {dateRange.from ? format(dateRange.from, 'PPP', { locale: de }) : 'Startdatum'}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={dateRange.from}
                                        onSelect={(date) => {
                                            if (date) {
                                                setDateRange((prev) => ({ ...prev, from: date }))
                                                setFromOpen(false)
                                            }
                                        }}
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div className="space-y-2">
                            <Label>Bis</Label>
                            <Popover open={toOpen} onOpenChange={setToOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={cn(
                                            'w-[240px] justify-start text-left font-normal',
                                            !dateRange.to && 'text-muted-foreground'
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {dateRange.to ? format(dateRange.to, 'PPP', { locale: de }) : 'Enddatum'}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={dateRange.to}
                                        onSelect={(date) => {
                                            if (date) {
                                                setDateRange((prev) => ({ ...prev, to: date }))
                                                setToOpen(false)
                                            }
                                        }}
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    <div className="border rounded-md overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[50px]">
                                        <Checkbox
                                            checked={
                                                billableSessions.length > 0 &&
                                                billableSessions.filter(s => !s.is_billed).length > 0 &&
                                                selectedSessionIds.size === billableSessions.filter(s => !s.is_billed).length
                                            }
                                            onCheckedChange={(checked: boolean) => handleSelectAll(checked)}
                                        />
                                    </TableHead>
                                    <TableHead>Datum</TableHead>
                                    <TableHead>Kurs</TableHead>
                                    <TableHead className="text-right">Teilnehmer</TableHead>
                                    <TableHead className="text-right">Betrag</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sessionsQuery.isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8">
                                            <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                                            <p className="text-sm text-muted-foreground mt-2">Lade Termine...</p>
                                        </TableCell>
                                    </TableRow>
                                ) : billableSessions.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                            Für diesen Zeitraum wurden keine Kurstermine gefunden.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    billableSessions.map((session) => (
                                        <TableRow key={session.id} className={cn(session.is_billed && "bg-muted/50 opacity-70")}>
                                            <TableCell>
                                                <Checkbox
                                                    checked={selectedSessionIds.has(session.id)}
                                                    onCheckedChange={() => !session.is_billed && toggleSession(session.id)}
                                                    disabled={session.is_billed}
                                                />
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {format(parseISO(session.starts_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                                            </TableCell>
                                            <TableCell>
                                                <div className="font-medium text-sm flex items-center gap-2">
                                                    {session.courses?.name}
                                                    {session.is_billed && (
                                                        <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded uppercase font-bold text-muted-foreground">Berechnet</span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-muted-foreground">{session.courses?.level}</div>
                                            </TableCell>
                                            <TableCell className="text-right text-sm">
                                                {session.booking_count}
                                            </TableCell>
                                            <TableCell className="text-right font-medium text-sm">
                                                {(session.amount_cents / 100).toFixed(2)} €
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
                {billableSessions.length > 0 && (
                    <CardFooter className="flex justify-between items-center bg-muted/30 py-4">
                        <div className="text-sm text-muted-foreground">
                            {selectedSessionIds.size} Termine ausgewählt
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="text-right">
                                <div className="text-xs text-muted-foreground uppercase font-semibold">Gesamtbetrag</div>
                                <div className="text-2xl font-bold">{(totalAmount / 100).toFixed(2)} €</div>
                            </div>
                            <Button
                                onClick={handleGenerate}
                                disabled={selectedSessionIds.size === 0 || createMutation.isPending}
                                className="gap-2"
                            >
                                {createMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <FileText className="h-4 w-4" />
                                )}
                                Honorarnote erstellen
                            </Button>
                        </div>
                    </CardFooter>
                )}
            </Card>
        </div>
    )
}
